import { useNavigate } from 'react-router-dom'
import {
  Users,
  Store,
  Building2,
  Crown,
  CreditCard,
  CheckCircle2,
  Clock,
  ArrowUpRight,
  ShieldAlert,
  Zap,
  TrendingUp,
  UserPlus,
} from 'lucide-react'
import { Card, CardContent, Button, Badge } from '@/components/ui'
import { SectionHeading } from '@/components/common'
import { appRoutes } from '@/constants'
import { useAdminStats } from '../../hooks/use-admin'

export function DashboardPage() {
  const navigate = useNavigate()
  const { data: stats, isLoading, error } = useAdminStats()

  return (
    <div className="space-y-8">
      {/* Header */}
      <SectionHeading
        eyebrow="Control Center"
        title="Platform Overview"
        description="Real-time performance metrics, user registrations, pending approval queues, and financial summary."
        align="left"
      />

      {/* Primary KPI Metric Cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {/* Total Users */}
        <Card className="relative overflow-hidden border-border/80 bg-card/80 backdrop-blur-xl">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Total Platform Users
              </span>
              <span className="flex size-9 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <Users className="size-5" />
              </span>
            </div>
            <div className="mt-3">
              {isLoading ? (
                <div className="h-8 w-24 animate-pulse rounded bg-muted/60" />
              ) : (
                <p className="font-display text-3xl font-extrabold text-foreground tracking-tight">
                  {stats?.users.total.toLocaleString() ?? 0}
                </p>
              )}
            </div>
            <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
              <span>Sellers: {stats?.users.sellers ?? 0}</span>
              <span>Dealers: {stats?.users.dealers ?? 0}</span>
            </div>
          </CardContent>
        </Card>

        {/* Pending Requests */}
        <Card className="relative overflow-hidden border-border/80 bg-card/80 backdrop-blur-xl">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Pending Approvals
              </span>
              <span className="flex size-9 items-center justify-center rounded-xl bg-amber-500/10 text-amber-500">
                <Clock className="size-5" />
              </span>
            </div>
            <div className="mt-3">
              {isLoading ? (
                <div className="h-8 w-24 animate-pulse rounded bg-muted/60" />
              ) : (
                <p className="font-display text-3xl font-extrabold text-amber-500 tracking-tight">
                  {stats?.approvals.pending ?? 0}
                </p>
              )}
            </div>
            <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
              <span>Requires Verification</span>
              <Button
                variant="link"
                size="sm"
                className="h-auto p-0 text-xs text-primary font-semibold"
                onClick={() => navigate(appRoutes.admin.sellerRequests)}
              >
                Review →
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Active Subscriptions */}
        <Card className="relative overflow-hidden border-border/80 bg-card/80 backdrop-blur-xl">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Active Subscriptions
              </span>
              <span className="flex size-9 items-center justify-center rounded-xl bg-emerald-500/10 text-emerald-500">
                <Crown className="size-5" />
              </span>
            </div>
            <div className="mt-3">
              {isLoading ? (
                <div className="h-8 w-24 animate-pulse rounded bg-muted/60" />
              ) : (
                <p className="font-display text-3xl font-extrabold text-foreground tracking-tight">
                  {stats?.subscriptions.active.toLocaleString() ?? 0}
                </p>
              )}
            </div>
            <div className="mt-2 text-xs text-muted-foreground">
              Total Plans Sold: {stats?.subscriptions.total ?? 0}
            </div>
          </CardContent>
        </Card>

        {/* Total Revenue */}
        <Card className="relative overflow-hidden border-border/80 bg-card/80 backdrop-blur-xl">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Total Revenue
              </span>
              <span className="flex size-9 items-center justify-center rounded-xl bg-blue-500/10 text-blue-500">
                <CreditCard className="size-5" />
              </span>
            </div>
            <div className="mt-3">
              {isLoading ? (
                <div className="h-8 w-24 animate-pulse rounded bg-muted/60" />
              ) : (
                <p className="font-display text-3xl font-extrabold text-foreground tracking-tight">
                  ₹{(stats?.payments.revenue ?? 0).toLocaleString('en-IN')}
                </p>
              )}
            </div>
            <div className="mt-2 text-xs text-muted-foreground">
              Successful Payments: {stats?.payments.paid ?? 0}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Action Shortcuts */}
      <Card className="border-border/80 bg-card/80 backdrop-blur-xl">
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center gap-2">
            <Zap className="size-5 text-primary" />
            <h3 className="font-display text-base font-bold text-foreground">
              Quick Administrative Actions
            </h3>
          </div>

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <button
              type="button"
              onClick={() => navigate(appRoutes.admin.sellerRequests)}
              className="flex items-center justify-between gap-3 rounded-xl border border-border/80 bg-background/60 p-4 text-left transition-all hover:border-primary hover:bg-accent/20"
            >
              <div className="flex items-center gap-3">
                <Store className="size-5 text-primary shrink-0" />
                <div>
                  <p className="text-sm font-bold text-foreground">Seller Approvals</p>
                  <p className="text-xs text-muted-foreground">Review pending seller accounts</p>
                </div>
              </div>
              <ArrowUpRight className="size-4 text-muted-foreground" />
            </button>

            <button
              type="button"
              onClick={() => navigate(appRoutes.admin.dealerRequests)}
              className="flex items-center justify-between gap-3 rounded-xl border border-border/80 bg-background/60 p-4 text-left transition-all hover:border-primary hover:bg-accent/20"
            >
              <div className="flex items-center gap-3">
                <Building2 className="size-5 text-primary shrink-0" />
                <div>
                  <p className="text-sm font-bold text-foreground">Dealer Approvals</p>
                  <p className="text-xs text-muted-foreground">Review business applications</p>
                </div>
              </div>
              <ArrowUpRight className="size-4 text-muted-foreground" />
            </button>

            <button
              type="button"
              onClick={() => navigate(appRoutes.admin.users)}
              className="flex items-center justify-between gap-3 rounded-xl border border-border/80 bg-background/60 p-4 text-left transition-all hover:border-primary hover:bg-accent/20"
            >
              <div className="flex items-center gap-3">
                <Users className="size-5 text-primary shrink-0" />
                <div>
                  <p className="text-sm font-bold text-foreground">User Directory</p>
                  <p className="text-xs text-muted-foreground">Search and manage all accounts</p>
                </div>
              </div>
              <ArrowUpRight className="size-4 text-muted-foreground" />
            </button>

            <button
              type="button"
              onClick={() => navigate(appRoutes.admin.subscriptions)}
              className="flex items-center justify-between gap-3 rounded-xl border border-border/80 bg-background/60 p-4 text-left transition-all hover:border-primary hover:bg-accent/20"
            >
              <div className="flex items-center gap-3">
                <Crown className="size-5 text-primary shrink-0" />
                <div>
                  <p className="text-sm font-bold text-foreground">Subscriptions</p>
                  <p className="text-xs text-muted-foreground">Monitor plans & active auto-renews</p>
                </div>
              </div>
              <ArrowUpRight className="size-4 text-muted-foreground" />
            </button>
          </div>
        </CardContent>
      </Card>

      {/* System Health & Status Banner */}
      <div className="rounded-2xl border border-emerald-500/20 bg-emerald-500/10 p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <CheckCircle2 className="size-6 text-emerald-500 shrink-0" />
          <div>
            <h4 className="font-display text-sm font-bold text-foreground">
              Platform Backend & Security Guard operational
            </h4>
            <p className="text-xs text-muted-foreground">
              Role-based access control, Prisma database connection, and authorization gates are active.
            </p>
          </div>
        </div>
        <Badge variant="success" className="shrink-0">
          All Services Online
        </Badge>
      </div>
    </div>
  )
}
